import { Button, GridLayout } from '@Common/Controls';
import { BackgroundGradientStop, GradientBackground, LinearGradientBackground } from '@InstanceFactory';
import { MulticastDelegate } from '@Common/Core/MulticastDelegate';
import StylesHelper from '@Common/Helpers/StylesHelper';
import { GradientStopUnit } from './GradientStopUnit';
import { GradientStopControl } from '@Common/Controls/GradientEditor/GradientStopControl';
import { enumerable } from '@Common/Core/CoreDecorator';

export class GradientSliderControl {
    private readonly root: JQuery;
    private gradient: JQuery;
    private readonly colors: JQuery;
    private units: GradientStopUnit[] = [];
    private currentStop: GradientStopUnit = null;
    private stopCtrl: GradientStopControl = new GradientStopControl();
    private readonly remove: Button;
    private readonly add: Button;
    private _value: GradientBackground;
    private background: LinearGradientBackground = LinearGradientBackground.create();
    private onChanged: MulticastDelegate = new MulticastDelegate();

    @enumerable
    public get changedEvent(): MulticastDelegate {
        return this.onChanged;
    }

    private grid: GridLayout;
    private setting = {
        stopWidth: 15,
        stopHeight: 15,
        defaultStopColor: 'rgba(255, 255, 255, 1)'
    };

    constructor() {
        this.background.startX = 0;
        this.background.endX = 1;
        this.root = $('<div>').addClass('gradientEditor-container').css('height', '45px');
        this.gradient = $('<div>')
            .addClass('gradientEditor-gradient')
            .appendTo(this.root)
            .css({
                height: '25px',
                'margin-left': this.setting.stopWidth / 2,
                'margin-right': this.setting.stopWidth / 2,
                'margin-bottom': 2
            });
        this.colors = $('<div>').addClass('gradientEditor-colors').appendTo(this.root).css({
            height: '15px'
        });

        this.remove = new Button({ text: strings.gradientEditor.remove, enabled: false });
        this.remove.onClick(() => {
            this.removeCurrent();
            this.setControlsState(false);
            this.stopCtrl.value((this.currentStop = null));
            this.update();
            this.onChanged.fire(this);
        });

        this.setControlsState(false);

        this.add = new Button({ text: strings.gradientEditor.add });
        this.add.onClick(() => {
            const newStop = { color: this.setting.defaultStopColor, offset: 0.4 };
            this._value.gradientStops.push(newStop);
            this.addStop(newStop, true);
            this.update();
            this.onChanged.fire(this);
        });

        this.grid = new GridLayout({
            rows: [{ style: { height: 'auto' } }, { style: { height: 'auto' } }, { style: { height: 'auto' } }, { style: { height: '*' } }],
            columns: [{ style: { width: '*' } }, { style: { width: 'auto' } }, { style: { width: 'auto' } }],
            cells: [
                { rowIndex: 0, columnIndex: 0, colSpan: 3, content: this.root, style: { padding: '0 5px' } },
                { rowIndex: 1, columnIndex: 0, colSpan: 3, content: this.stopCtrl, style: { padding: '0 5px' } },
                { rowIndex: 2, columnIndex: 1, content: this.add, style: { padding: '0 5px' } },
                { rowIndex: 2, columnIndex: 2, content: this.remove, style: { padding: '0 5px' } }
            ]
        });

        this.stopCtrl.changedEvent.add(() => {
            this.update();
            this.currentStop.update();
            this.onChanged.fire(this);
        });
    }

    private setControlsState(state: boolean): void {
        this.stopCtrl.enabled(state);
        this.remove.enabled(state);
    }

    private removeCurrent(): void {
        this._value.gradientStops.remove(this.currentStop.getValue());
        this.units.remove(this.currentStop);
        this.currentStop.remove();
    }

    private activate(unit: GradientStopUnit): void {
        if (this.currentStop != null && this.currentStop !== unit) {
            this.currentStop.diactivate();
        }
        unit.activate();
        this.currentStop = unit;
        this.setControlsState(true);
        this.stopCtrl.value(unit.getValue());
    }

    private addStop(stop: BackgroundGradientStop, activate: boolean = false): void {
        const unit = new GradientStopUnit(
            stop,
            {
                width: this.setting.stopWidth,
                height: this.setting.stopHeight
            },
            this.colors
        );
        unit.changedEvent.add(() => {
            this.stopCtrl.value(unit.getValue());
            this.update();
            this.onChanged.fire(this);
        });

        unit.selectEvent.add(() => {
            this.activate(unit);
        });
        this.units.push(unit);
        if (activate) {
            this.activate(unit);
        }
    }

    private update(): void {
        this.background.gradientStops = this._value.gradientStops;
        const value = StylesHelper.getBackground(this.background);
        this.gradient.css('background', value);
    }

    public getControl(): JQuery {
        return this.grid.getControl();
    }

    public adjust(w: number, h: number): void {
        this.grid.adjust(w, h);
        this.units.forEach((su: GradientStopUnit) => {
            su.update();
        });
    }

    private reset(): void {
        this.units.forEach((u: GradientStopUnit) => {
            u.remove();
        });
        this.units = [];
        this.currentStop = null;
    }

    public value(val?: GradientBackground): GradientBackground {
        if (arguments.length !== 0) {
            this.reset();

            this._value = val;
            this._value.gradientStops.forEach((gs: BackgroundGradientStop) => {
                this.addStop(gs);
            });
            this.update();
        }
        return this._value;
    }
}
